import { useReducer } from "react";
import styled, { x } from "@xstyled/styled-components";

import { AddButton } from "../AddButton";
import { useJobEditFormikContext } from "../../useHandlers";

import { Restrictions } from "./Restrictions";
import { RemoteInfo } from "./RemoteInfo";
import { OfficeField } from "./OfficeField";

import {
  Checkbox,
  ErrorContainer,
  ErrorText,
  FieldHeading,
  Fieldset,
  Spacing,
} from "@otta/design";

export const isRemote = (location: string) => location.startsWith("REMOTE_");

export const allRemote = (locations?: string[]) =>
  locations?.every(isRemote) ?? true;

const Panels = styled.div`
  display: flex;
  flex-direction: column;
  gap: lg;

  &:empty {
    display: none;
  }
`;

export function Location() {
  const form = useJobEditFormikContext();

  const handleChange = (name: string, checked: boolean) => {
    form.setFieldValue(name, checked, true);
  };

  const [showExtraFields, setShowExtraFields] = useReducer(
    () => true,
    !!form.values.locationRestrictions || !!form.values.locationInformation
  );

  const hasLocation =
    form.values.hasOfficeLocationPreferences ||
    form.values.hasRemoteLocationPreferences;

  return (
    <Fieldset
      required
      legend="Location"
      spacing={null}
      advice="Select the ways candidates for this role would be able to work."
    >
      <x.div display="flex" flexDirection="column" gap="lg" mt="lg">
        <x.div display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="sm">
          <Switch
            name="hasOfficeLocationPreferences"
            checked={!!form.values.hasOfficeLocationPreferences}
            onChange={handleChange}
          >
            Office/Hybrid
          </Switch>
          <Switch
            name="hasRemoteLocationPreferences"
            checked={!!form.values.hasRemoteLocationPreferences}
            onChange={handleChange}
          >
            Fully remote
          </Switch>
        </x.div>

        <Panels>
          {hasLocation && (
            <>
              <x.div h="1px" backgroundColor="beige-200" my="lg" />
              <FieldHeading
                label="Working locations"
                required
                advice="(at least one required)"
              />
            </>
          )}
          {!hasLocation && form.errors.locationValidity && (
            <ErrorContainer>
              <ErrorText>{form.errors.locationValidity}</ErrorText>
            </ErrorContainer>
          )}
          {hasLocation && <OfficeField />}
          {(form.values.hasOfficeLocationPreferences ||
            form.values.hasRemoteLocationPreferences) && (
            <>
              {!showExtraFields && (
                <AddButton onClick={setShowExtraFields} isOptional>
                  Add location restrictions or other information
                </AddButton>
              )}
              {showExtraFields && (
                <Spacing size={1}>
                  <RemoteInfo />
                  <Restrictions />
                </Spacing>
              )}
            </>
          )}
        </Panels>
      </x.div>
    </Fieldset>
  );
}

function Switch({
  children,
  name,
  checked,
  onChange,
}: {
  children: React.ReactNode;
  name: string;
  checked: boolean;
  onChange: (name: string, checked: boolean) => void;
}): React.ReactElement {
  return (
    <x.label
      position="relative"
      py="md"
      borderRadius="md"
      backgroundColor="beige-200"
      textAlign="center"
      cursor="pointer"
    >
      <x.div
        position="absolute"
        left="xl"
        h={18}
        top="50%"
        transform="translateY(-50%)"
      >
        <Checkbox
          label=""
          onChange={checked => onChange(name, checked)}
          checked={checked}
        />
      </x.div>
      {children}
    </x.label>
  );
}
